import React, { useEffect, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { extensions } from '../../../../Utils/reportExtension';
import { useClinicDropdowns } from '../../../../Utils/useClinicDropdowns';
import { capitalizeFirstLetterOfEachWord, convertBlobToBase64, formatDateToDDMMYYYY } from '../../../../Utils/utils';
import { AppDispatch, RootState } from '../../../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { updateReport } from '../../../../rtk-apis/reports/updateReport';
import '../../../reports/styles.scss';
import { Loader } from '../../../../components';
import { clearState } from '../../../../store/slices/reports/updateReport';
import useDebounce from '../../../../components/useDebounce';
import { MenuBar } from '../../../../pages/reports/ReportsLayout';
import AssignDoctorModal from '../assignToModal';
import AssignSonographerModal from '../assignSonographerModal';
import { StressBullsEyePlot } from '../../../../pages/reports/components/StressBullsEyePlot';
import { RestBullsEyePlot } from '../../../../pages/reports/components/RestBullsEyePlot';
import { Apical2Ch } from '../../../../pages/reports/components/Apical2Ch';
import { Apical3Ch } from '../../../../pages/reports/components/Apical3Ch';
import { Apical4Ch } from '../../../../pages/reports/components/Apical4Ch';
import { StressApical2Ch } from '../../../../pages/reports/components/StressApical2Ch';
import { StressApical3Ch } from '../../../../pages/reports/components/StressApical3Ch';
import { StressApical4Ch } from '../../../../pages/reports/components/StressApical4Ch';
import { TargetCodes } from '../../../../pages/reports/components/TargetCodes';
import { canEditMedicalData, isAssignedDoctor } from '../../../../Utils/reportPermissions';
import AttachClipsModal from '../../../../pages/reports/components/AttachClipsModal';
import { useAutoAttachClips } from '../../../../pages/reports/components/useAutoAttachClips';
import { useChainEditing } from '../../../../pages/reports/components/useChainEditing';
import { ChainFieldPopup, ChainNoticeBar } from '../../../../pages/reports/components/ChainFieldPopup';
import { stripChainMarks } from '../../../../Utils/chainDocSanitize';
import {
  CHAIN_POPUP_HEIGHT,
  CHAIN_POPUP_WIDTH,
  NUMERIC_POPUP_HEIGHT,
  NUMERIC_POPUP_WIDTH,
  SUGGESTION_POPUP_HEIGHT,
  SUGGESTION_POPUP_WIDTH,
  getReportPopupPosition,
} from '../../../reports/components/reportPopupPosition';
import { getStudyMarkingsApi } from '../../../../rtk-apis/reports/getStudyMarkingsApi';
import { getStressValues } from '../../../../rtk-apis/reports/getStressValues';
import { clearState as clearStressValues } from '../../../../store/slices/reports/getStressValues';
import {
  NUMERIC_REPORT_FIELDS,
  NUMERIC_EMPTY_MARKER,
  isNumericFieldKey,
  numericAliasGroup,
  wrapNumericField,
  isCompleteNumericEntry,
} from '../../../../pages/reports/numericReportFields';
import { applyNumericUpdates } from '../../../../Utils/numericFieldSync';
import { applyDynamicFieldUpdate } from '../../../../Utils/dynamicFieldSync';
import { joinValueAndUnit, splitValueAndUnit } from '../../../../Utils/measurementValueParts';
import { applyPhraseUpdates, markPhraseUserOwned } from '../../../../Utils/phraseSlotSync';
import { PEAK_HR_KEY, deriveStressHeartRateValues } from '../../../../Utils/stressHeartRate';
import type { DetectedArrhythmia } from '../../../../Utils/arrhythmiaVocabulary';
import { setReportStatusCell } from '../../../../Utils/reportStatusCell';


const extractTextFromNode = (node: any): string => {
  if (!node) return '';
  if (node.type === 'text') return node.text || '';
  if (Array.isArray(node.content)) return node.content.map(extractTextFromNode).join('');
  return '';
};

const injectNameIntoHTML = (html: string, name: string, labelPattern: RegExp): string => {
  if (!html || !name) return html;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const tds = doc.querySelectorAll('td');
    for (let i = 0; i < tds.length - 1; i++) {
      if (labelPattern.test(tds[i].textContent?.trim() || '')) {
        const valueP = tds[i + 1].querySelector('p');
        if (valueP) {
          valueP.textContent = name;
        } else {
          tds[i + 1].textContent = name;
        }
        break;
      }
    }
    return doc.body.querySelector('div').innerHTML;
  } catch {
    return html;
  }
};

const injectCardiologistIntoHTML = (html: string, name: string): string =>
  injectNameIntoHTML(html, name, /cardiologist/i);

/** Same treatment for the Sonographer row (client request, 26 Aug). */
const injectSonographerIntoHTML = (html: string, name: string): string =>
  injectNameIntoHTML(html, name, /sonographer/i);

const injectCardiologistIntoJSON = (json: any, name: string): any => {
  if (!json || !name) return json;
  const clone = JSON.parse(JSON.stringify(json));
  const traverse = (node: any): boolean => {
    if (!node) return false;
    if (node.type === 'tableRow' && Array.isArray(node.content)) {
      for (let i = 0; i < node.content.length - 1; i++) {
        const labelText = extractTextFromNode(node.content[i]).trim().toLowerCase();
        if (labelText.includes('cardiologist')) {
          const valueCell = node.content[i + 1];
          if (valueCell?.content) {
            valueCell.content[0] = {
              type: 'paragraph',
              content: [{ type: 'text', text: name }],
            };
            return true;
          }
        }
      }
    }
    if (Array.isArray(node.content)) {
      for (const child of node.content) {
        if (traverse(child)) return true;
      }
    }
    return false;
  };
  traverse(clone);
  return clone;
};

const ReportLayout = props => {
  // const { editor } = useCurrentEditor();
  // States for suggestion functionality
  const [suggestions, setSuggestions] = useState([]);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [selectedSpan, setSelectedSpan] = useState(null);
  const { dropdownMap } = useClinicDropdowns();
  const dropdownMapRef = useRef(dropdownMap);
  dropdownMapRef.current = dropdownMap;

  // Numeric slot editing — the typed-value counterpart to the phrase dropdown above.
  // Held separately so the two popups can never be open at once. Ported from
  // `ReportsLayout` on 2 Sep: these spans rendered here (amber dotted) but nothing was
  // listening for the click, so the stress figures were read-only on this page while the
  // studies page let them be typed and synced.
  const [numericSlot, setNumericSlot] = useState<{
    from: number;
    to: number;
    key: string;
  } | null>(null);
  const [numericPosition, setNumericPosition] = useState<{ top: number; left: number } | null>(
    null
  );
  const [numericDraft, setNumericDraft] = useState('');
  // Escape blurs the input as it closes the popup; without this the blur handler would
  // commit the value the clinician just discarded.
  const numericCancelledRef = useRef(false);
  useEffect(() => {
    numericCancelledRef.current = false;
  }, [numericSlot?.key, numericSlot?.from]);

  // Dynamic (clinic-created) field editing — same popup and sync as `ReportsLayout`.
  const [dynamicFieldSlot, setDynamicFieldSlot] = useState<{
    key: string;
    label: string;
    originalValue: string;
    fieldType: 'text' | 'dropdown' | 'number';
    options: string[];
    unit: string;
  } | null>(null);
  const [dynamicFieldPosition, setDynamicFieldPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [dynamicFieldDraft, setDynamicFieldDraft] = useState('');
  const dynamicFieldCancelledRef = useRef(false);
  useEffect(() => {
    dynamicFieldCancelledRef.current = false;
  }, [dynamicFieldSlot?.key]);
  /**
   * Which attachment filled the {{stress_*}} values, and in what format. A value read
   * from the structured XML export is not as trustworthy as one the clinician typed, nor
   * as trustworthy as the same value scraped from a printed PDF — so it is shown in the
   * edit popup, the moment someone is deciding whether to accept the number.
   */
  const [stressSource, setStressSource] = useState<{ format: string; file: string } | null>(null);
  /** The arrhythmia the CardioSoft export recorded, if it recorded one. */
  const [stressArrhythmia, setStressArrhythmia] = useState<DetectedArrhythmia | null>(null);

  const chainDispatch = useDispatch<AppDispatch>();
  const stressValuesResponse = useSelector((state: RootState) => state.getStressValuesResponse);
  const reportDoc = props?.reportTemplate?.data || {};
  const studyUID = reportDoc?.studyInstanceUID;
  const reportKind = props?.reportTemplate?.templateName || reportDoc?.reportType || '';
  const isStressReport = /stress/i.test(String(reportKind));

  /**
   * Stress reports only: pull the CardioSoft values parsed from the attached export.
   *
   * Only the ARRHYTHMIA and the provenance are taken from it here. The `{{stress_*}}`
   * placeholders were already filled when the report was created, and this page's job is
   * to edit the saved body — re-injecting values would fight the clinician's own edits.
   */
  useEffect(() => {
    // Cleared FIRST, every time, and not only before a fetch. The slice is shared with
    // the studies page and holds the LAST study's answer, which carries no study
    // identifier — so a report opened after a stress study would otherwise read the
    // previous study's arrhythmia off a stale SUCCESS on mount and, if it happened to be
    // another stress report, write the wrong finding into it.
    chainDispatch(clearStressValues());
    if (!isStressReport) return;
    if (!studyUID && !reportDoc?._id) return;
    chainDispatch(
      getStressValues(studyUID ? { studyInstanceUID: studyUID } : { reportId: reportDoc._id })
    );
  }, [isStressReport, studyUID, reportDoc?._id]);

  useEffect(() => {
    if (!isStressReport) return;
    if (stressValuesResponse?.status !== 'SUCCESS') return;
    const res: any = stressValuesResponse?.data;
    if (!res?.isStress) return;
    // Read out of the export's narrative by the backend — see `stressArrhythmia.js`.
    setStressArrhythmia((res.meta && res.meta.arrhythmia) || null);
    setStressSource({
      format: String(res.sourceFormat || 'xml').toUpperCase(),
      file: res.source || '',
    });
  }, [stressValuesResponse?.status, stressValuesResponse?.data]);

  /**
   * Field chaining + smart wording on this page (Nikhil, 1 Sep: "in reports page its not
   * working"). The engine reads the report body, not the study's SR, so it needs no viewer
   * loaded here — see `useChainEditing` for why that works and what it costs.
   */
  const editorRef = useRef<any>(null);
  const chain = useChainEditing({
    editorRef,
    // Only what the report body does not print. Sex is the one the wording rules need and
    // the body never shows; every measurement comes from the document itself.
    seedValues: { sex: props?.reportTemplate?.data?.sex },
    sex: props?.reportTemplate?.data?.sex,
    atrialFibrillation: props?.atrialFibrillation === true,
    dropdownMap,
    arrhythmia: stressArrhythmia,
    canWrite: props?.isUpdateable === true,
    // The chain's writes ARE the clinician's edit, propagated, so they must reach
    // autosave. The editor's own `update` event does that; this only has to make sure a
    // change made inside a single transaction is not missed.
    onDocumentChanged: () => {
      if (!editorRef.current) return;
      const updated = stripChainMarks(editorRef.current.getJSON());
      props?.setEditorContent(updated);
      props?.setUpdatedTemplateContent(updated);
    },
  });
  const chainWritingRef = chain.chainWritingRef;
  /**
   * `editorProps.handleClick` is built once when the editor is created, so it closes over
   * the FIRST `chain` object. Reading through a ref keeps the click on a measurement wired
   * to the current handlers instead of a stale copy.
   */
  const chainRef = useRef(chain);
  chainRef.current = chain;

  const editor = useEditor({
    extensions,
    content: props.editorContent,
    editable: props?.isUpdateable,
    editorProps: {
      attributes: {
        spellcheck: 'false',
      },
      handleClick: (view, pos, event) => {
        if (!view.editable) return;
        // Cardiologist cell click → open assign modal
        const td = (event.target as HTMLElement).closest('td');
        if (td) {
          const prevTd = td.previousElementSibling as HTMLElement;
          if (prevTd && /cardiologist/i.test(prevTd.textContent?.trim() || '')) {
            props?.onAssignCardiologist?.();
            return;
          }
          // Sonographer cell → open the sonographer picker. The label cell opens it too,
          // because on a report with no sonographer yet the value cell is empty and
          // barely clickable.
          if (props?.onSelectSonographer) {
            const isSonoValue = prevTd && /sonographer/i.test(prevTd.textContent?.trim() || '');
            const isSonoLabel = /sonographer:/i.test(td.textContent?.trim() || '');
            if (isSonoValue || isSonoLabel) {
              props.onSelectSonographer();
              return;
            }
          }
        }
        // Chained measurement value — click to retype the figure; committing pushes the
        // change through everything derived from it. Checked BEFORE the phrase dropdown:
        // a chained value is never a phrase slot, and the two popups must not both open.
        const chainTarget = (event.target as HTMLElement).closest('span[data-chain-field]');
        if (chainTarget) {
          event.stopPropagation();
          setDropdownPosition(null);
          setSuggestions([]);
          if (chainRef.current.openChainField(chainTarget as HTMLElement)) {
            return;
          }
        }

        // Dynamic field — a dropdown with options opens as a list, anything else as an input.
        const dynamicTarget = (event.target as HTMLElement).closest(
          'span[data-dynamic-field]'
        ) as HTMLElement | null;
        if (dynamicTarget) {
          event.stopPropagation();
          const dynamicKey = dynamicTarget.classList[0];
          if (!dynamicKey) {
            return;
          }
          const currentValue = dynamicTarget.textContent || '';
          type DynamicFieldType = 'text' | 'dropdown' | 'number';
          const fieldType =
            (dynamicTarget.getAttribute('data-field-type') as DynamicFieldType | null) || 'text';
          let options: string[] = [];
          try {
            options = JSON.parse(dynamicTarget.getAttribute('data-options') || '[]');
          } catch {
            options = [];
          }
          const label = dynamicTarget.getAttribute('title') || dynamicKey;
          // "[Label]" is the unfilled placeholder, not a value.
          const isPlaceholder = currentValue === `[${label}]`;
          setSuggestions([]);
          setDropdownPosition(null);
          setNumericPosition(null);
          setNumericSlot(null);
          setDynamicFieldDraft(
            isPlaceholder
              ? ''
              : fieldType === 'number'
                ? splitValueAndUnit(currentValue).number
                : currentValue
          );
          setDynamicFieldSlot({
            key: dynamicKey,
            label,
            originalValue: currentValue,
            fieldType,
            options,
            unit: dynamicTarget.getAttribute('data-unit') || '',
          });
          const opensAsList = fieldType === 'dropdown' && options.length > 0;
          setDynamicFieldPosition(
            getReportPopupPosition(
              dynamicTarget,
              null,
              opensAsList ? SUGGESTION_POPUP_WIDTH : CHAIN_POPUP_WIDTH,
              opensAsList ? SUGGESTION_POPUP_HEIGHT : CHAIN_POPUP_HEIGHT
            )
          );
          return;
        }

        // Numeric slot — no vocabulary to offer, so it opens a typed-value editor.
        // Checked before the phrase dropdown for the same reason the chain target is:
        // a numeric slot is never a phrase slot and the popups must not both open.
        const numericTarget = (event.target as HTMLElement).closest(
          'span[data-editable-number]'
        ) as HTMLElement | null;
        if (numericTarget) {
          event.stopPropagation();
          const numericKey = numericTarget.classList[0];
          if (numericKey && isNumericFieldKey(numericKey)) {
            const numPos = view.posAtDOM(numericTarget, 0);
            const currentNumeric = numericTarget.textContent || '';
            setSuggestions([]);
            setDropdownPosition(null);
            setNumericDraft(currentNumeric === NUMERIC_EMPTY_MARKER ? '' : currentNumeric);
            setNumericSlot({
              from: numPos,
              to: numPos + currentNumeric.length,
              key: numericKey,
            });
            setNumericPosition(
              getReportPopupPosition(
                numericTarget,
                null,
                NUMERIC_POPUP_WIDTH,
                NUMERIC_POPUP_HEIGHT
              )
            );
            return;
          }
          return;
        }

        const target = event.target.closest('span[data-editable]');
        if (!target) {
          return;
        }
        event.stopPropagation();
        const spanClass = target.classList[0];
        if (!spanClass) {
          return;
        }
        const editorPos = view.posAtDOM(target, 0);
        setSuggestions(dropdownMapRef.current[spanClass] || []);
        setSelectedSpan({
          from: editorPos,
          to: editorPos + target.textContent.length,
          className: spanClass,
          element: target,
        });
        setDropdownPosition(
          getReportPopupPosition(
            target as HTMLElement,
            null,
            SUGGESTION_POPUP_WIDTH,
            SUGGESTION_POPUP_HEIGHT
          )
        );
      },
    },
  });

  // Kept current on every render so the chain engine's callbacks — created before
  // `useEditor` above, since the editor's click handler closes over them — always see the
  // live editor rather than the null it returns on the first render.
  editorRef.current = editor;

  // Function to handle word replacement
  const handleReplaceWord = suggestion => {
    if (!editor?.isEditable) return;
    if (editor && selectedSpan) {
      const scrollPosition = editor.view.dom.scrollTop;
      editor
        .chain()
        .deleteRange({ from: selectedSpan.from, to: selectedSpan.to })
        .insertContentAt(
          selectedSpan.from,
          `<span data-editable class='${selectedSpan.className}'>${suggestion}</span>`
        )
        .setTextSelection(selectedSpan.from + suggestion.length)
        .run();

      // Every OTHER copy of the same sentence. `la-no` prints twice in 29 of the 32 ECHO
      // templates and `physiological` in 27, so picking a word in the Findings used to
      // leave the Conclusions copy still reading the old one — in a report the clinician
      // then signs. Nikhil, 02 Sep: "if they're using the same thing up there and up
      // there, if you change one of them, the other should change".
      //
      // Only the copies describing the SAME finding. The Aortic Valve line prints
      // `la-no` twice, before "aortic regurgitation" and before "aortic stenosis", so
      // matching on the class alone answered both from one pick.
      //
      // Marked as chain writing for the duration so the editor's `update` handler cannot
      // fire between the two writes and push a half-updated document out for autosave;
      // the finished result is pushed explicitly below.
      chainWritingRef.current = true;
      try {
        applyPhraseUpdates(
          editor,
          { [selectedSpan.className]: suggestion },
          false,
          selectedSpan.from
        );
        // Their choice, recorded in the document so it survives the save. The engine
        // will not write to this sentence again on this report.
        markPhraseUserOwned(editor, selectedSpan.className, selectedSpan.from);
        // Right atrial pressure is picked from a dropdown but it is also a measurement,
        // so the chain writes it — through the hook, which owns the write gate and the
        // value snapshot. A click anywhere else is not one, and it says so.
        chainRef.current.applyRapPick(selectedSpan.from, suggestion);
      } finally {
        chainWritingRef.current = false;
      }

      // The pick and its copies DO belong in the saved report — this is the clinician's
      // own edit, propagated — so push the result out for autosave.
      const pickedContent = stripChainMarks(editor.getJSON());
      props?.setEditorContent(pickedContent);
      props?.setUpdatedTemplateContent(pickedContent);

      setTimeout(() => {
        editor.view.dom.scrollTop = scrollPosition;
      }, 0);
      setDropdownPosition(null);
    }
  };

  /**
   * Write a typed value back into its numeric slot, then into every other occurrence.
   *
   * Clearing the field restores the "--" marker rather than deleting the span: an empty
   * span collapses and leaves nothing to click, so the slot would be lost from the report
   * for good.
   *
   * Typing the peak heart rate additionally recalculates the percentage of age-predicted
   * maximum HR and writes it EVERYWHERE it prints — the Stress Findings sentence and the
   * Conclusions bullet both carry it. Dr Shashi, 31 Aug: "sono usually inputs peak HR
   * hence, system automatically calculates the percentage. This is done on both
   * conclusion and body." See `stressHeartRate.ts` for the formula and for why an
   * underivable value leaves the report untouched.
   */
  const handleCommitNumeric = () => {
    if (!editor?.isEditable || !numericSlot) return;

    const trimmed = numericDraft.trim();
    const nextValue = trimmed || NUMERIC_EMPTY_MARKER;
    const committedKey = numericSlot.key;
    const scrollPosition = editor.view.dom.scrollTop;
    editor
      .chain()
      .deleteRange({ from: numericSlot.from, to: numericSlot.to })
      .insertContentAt(numericSlot.from, wrapNumericField(committedKey, nextValue))
      .setTextSelection(numericSlot.from + nextValue.length)
      .run();

    // Every OTHER occurrence of the same key, and of any key that means the same measured
    // value (`stress_peak_bp` and `stress_peak_sbp` are one figure under two placeholder
    // names). Nikhil, 02 Sep: "if something says stress resting, and it is anywhere else,
    // and if you change one of them, it should all change." The clinician only ever clicks
    // one of them. The slot just written holds `nextValue` already, so the no-change guard
    // inside `applyNumericUpdates` skips it.
    const propagated: Record<string, string> = {};
    numericAliasGroup(committedKey).forEach(key => {
      propagated[key] = nextValue;
    });
    applyNumericUpdates(editor, propagated);

    // Derived values, after the typed one is in the document so the derivation and the
    // report can never disagree about what was entered. A cleared field derives nothing
    // and leaves the percentage standing rather than wiping a figure still wanted.
    if (committedKey === PEAK_HR_KEY && trimmed) {
      const derived = deriveStressHeartRateValues(trimmed, reportDoc);
      if (Object.keys(derived).length > 0) {
        applyNumericUpdates(editor, derived);
      }
    }

    // These writes are the clinician's edit propagated, so unlike the chain's housekeeping
    // they must reach autosave. The transactions above fire the editor's own `update`
    // handler, which does that; this is belt-and-braces for the single-transaction case.
    const committedContent = stripChainMarks(editor.getJSON());
    props?.setEditorContent(committedContent);
    props?.setUpdatedTemplateContent(committedContent);

    setTimeout(() => {
      editor.view.dom.scrollTop = scrollPosition;
    }, 0);

    setNumericPosition(null);
    setNumericSlot(null);
    setNumericDraft('');
  };

  const closeDynamicField = () => {
    setDynamicFieldPosition(null);
    setDynamicFieldSlot(null);
    setDynamicFieldDraft('');
  };

  /** Writes a dynamic field's value into every copy of its key. `overrideDraft` = a dropdown pick. */
  const handleCommitDynamicField = (overrideDraft?: string) => {
    if (!editor?.isEditable || !dynamicFieldSlot) {
      closeDynamicField();
      return;
    }
    const { key, originalValue, fieldType, unit } = dynamicFieldSlot;
    const draft = overrideDraft !== undefined ? overrideDraft : dynamicFieldDraft;
    const nextValue = fieldType === 'number' ? joinValueAndUnit(draft, unit) : draft;
    // Empty would collapse the span and lose the field, so it counts as no change.
    if (nextValue.trim() && nextValue !== originalValue) {
      const scrollPosition = editor.view.dom.scrollTop;
      applyDynamicFieldUpdate(editor, key, nextValue);
      const committedContent = stripChainMarks(editor.getJSON());
      props?.setEditorContent(committedContent);
      props?.setUpdatedTemplateContent(committedContent);
      setTimeout(() => {
        editor.view.dom.scrollTop = scrollPosition;
      }, 0);
    }
    closeDynamicField();
  };

  // Effect for handling clicks outside the suggestion dropdown
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        !event.target.closest('.suggestion-dropdown') &&
        !event.target.closest('span[data-editable]')
      ) {
        setDropdownPosition(null);
        setSelectedSpan(null);
      }
      if (
        !event.target.closest('.numeric-field-popup') &&
        !event.target.closest('span[data-editable-number]')
      ) {
        setNumericPosition(null);
        setNumericSlot(null);
      }
      if (
        !event.target.closest('.dynamic-field-popup') &&
        !event.target.closest('span[data-dynamic-field]')
      ) {
        setDynamicFieldPosition(null);
        setDynamicFieldSlot(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Once the user has typed, a refetch-driven reload must NOT clobber their
  // edits (e.g. the post-status-change refetch re-runs loadContent).
  const userDirtyRef = useRef(false);
  const programmaticSetRef = useRef(false);
  useEffect(() => {
    userDirtyRef.current = false;
  }, [props?.reportTemplate?.data?._id]);

  useEffect(() => {
    const loadContent = async () => {
      // Never overwrite genuine user edits — but an EMPTY editor has nothing to
      // protect. Bailing while empty is how the report page got stuck blank
      // (a cleanup clearContent was misread as a user edit), so always reload then.
      if (userDirtyRef.current && editor && !editor.isEmpty) {
        return;
      }
      if (editor && props?.reportTemplate?.data?.templateHTML) {
        try {
          // Create a deep copy of the templateHTML to avoid mutating readonly properties
          const templateHTML = JSON.parse(JSON.stringify(props.reportTemplate.data.templateHTML));

          // Show the report's current status in its header cell (found by label).
          setReportStatusCell(templateHTML, props?.reportTemplate?.data?.status);

          const cardiologistName = props?.reportTemplate?.data?.reportAssignedTo;
          let contentToLoad = templateHTML;
          if (cardiologistName) {
            if (typeof templateHTML === 'string') {
              contentToLoad = injectCardiologistIntoHTML(templateHTML, cardiologistName);
            } else {
              contentToLoad = injectCardiologistIntoJSON(templateHTML, cardiologistName);
            }
          }

          await editor.setEditable(props?.isUpdateable);

          // Set the modified content in the editor
          programmaticSetRef.current = true;
          await editor.commands.setContent(contentToLoad);
          programmaticSetRef.current = false;

          // Update editor content state. Stripped for the same reason as the update
          // handler above — this is a save source too, via the autosave baseline.
          props.setEditorContent(stripChainMarks(contentToLoad));
          props.setUpdatedTemplateContent(stripChainMarks(contentToLoad));

          // Mark the chained values and capture the phrase baseline, NOW — with the body
          // actually in the editor. Doing this from a separate effect raced the await
          // above: it primed an empty document, so the baseline was empty, every phrase
          // slot read as "changed by a human", and the wording rules would have gone
          // permanently quiet on this page while looking like they had run.
          chainRef.current.primeDocument();
        } catch (error) {
          console.error('Error parsing or setting editor content:', error);
        }
      }
    };

    // Load content every time the reportTemplate changes
    loadContent();

    // Cleanup previous editor content when switching between patients.
    // clearContent() fires the editor's `update` event — without the programmatic
    // flag it would mark userDirtyRef, making the next loadContent() bail and
    // leaving the report permanently blank (e.g. when isUpdateable flips after load).
    return () => {
      programmaticSetRef.current = true;
      editor?.commands.clearContent();
      programmaticSetRef.current = false;
    };
  }, [editor, props?.reportTemplate?.data?.templateHTML, props?.isUpdateable]);

  /**
   * Run the chain from the document after typing settles. 600ms matches the studies page:
   * long enough that a value being typed digit by digit is not chained half-finished, short
   * enough to feel immediate. Sits ahead of the 2s autosave, so what gets saved is the
   * propagated result rather than the intermediate state.
   */
  const debouncedChainContent = useDebounce(props?.editorContent, 600);
  /**
   * `stressArrhythmia` and the dropdown count are dependencies because both arrive from
   * their own fetch, often AFTER the document has settled. Without them the arrhythmia
   * line and any sentence needing this clinic's vocabulary would stay as the template
   * wrote them until the clinician happened to type something. Re-running is safe: every
   * write goes through the untouched-baseline rule and a no-change proposal is dropped.
   */
  const dropdownCount = Object.keys(dropdownMap || {}).length;
  useEffect(() => {
    if (!editor || !debouncedChainContent) return;
    chainRef.current.runChainFromDocument();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, debouncedChainContent, stressArrhythmia, dropdownCount]);

  // Re-inject cardiologist name when it changes (after assign)
  useEffect(() => {
    if (!editor || !props?.reportAssignedTo) return;
    const html = editor.getHTML();
    const injected = injectCardiologistIntoHTML(html, props.reportAssignedTo);
    if (injected !== html) {
      editor.commands.setContent(injected);
    }
  }, [props?.reportAssignedTo]);

  // Re-inject sonographer name when it changes (after selection)
  useEffect(() => {
    if (!editor || !props?.sonographer) return;
    const html = editor.getHTML();
    const injected = injectSonographerIntoHTML(html, props.sonographer);
    if (injected !== html) {
      editor.commands.setContent(injected);
    }
  }, [props?.sonographer]);

  /**
   * Capture changes in the editor and update the specific section.
   *
   * ⚠️ `stripChainMarks` IS NOT OPTIONAL. Every save path on this page — the 2s
   * debounced autosave, the unmount flush, and the parent's finalize via
   * `setUpdatedTemplateContent` — reads the state written here, so this is the single
   * place the marks can be kept out of the database. TipTap validates stored JSON
   * against the running schema, and a report saved carrying a `chainField` mark opens
   * BLANK on any build without that mark. The marks are rebuilt on open by
   * `backfillChainMarks` instead, from the labels in the document.
   *
   * The engine's own writes are excluded from the dirty flag the same way a
   * programmatic `setContent` is: marking values on open must never make a report look
   * edited, or merely opening one would save it.
   */
  useEffect(() => {
    if (editor) {
      editor.on('update', () => {
        // Marking values for the chain, or writing recomputed ones, is housekeeping — not
        // a clinician edit. It must not propagate AT ALL, for two reasons: it would make
        // simply opening a report dirty and trigger an autosave that rewrites every stored
        // body, and — because stripping the marks yields a document identical to the one
        // before backfill, but a NEW object — it would re-trigger the debounced pass below
        // on its own output, forever. The engine pushes its own result through
        // `onDocumentChanged` when it has genuinely changed something.
        if (chainWritingRef.current) {
          return;
        }
        if (!programmaticSetRef.current) {
          userDirtyRef.current = true;
        }
        const updatedContent = stripChainMarks(editor.getJSON());
        props?.setEditorContent(updatedContent);
        props?.setUpdatedTemplateContent(updatedContent);
      });
    }
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <>
      <MenuBar editor={editor} />
      <div style={{ position: 'relative' }}>
        <EditorContent editor={editor} />
        <ChainFieldPopup
          slot={chain.chainSlot}
          position={chain.chainPosition}
          draft={chain.chainDraft}
          onDraftChange={chain.setChainDraft}
          onCommit={chain.commitChainField}
          onCancel={chain.closeChainPopup}
        />
        <ChainNoticeBar notice={chain.chainNotice} onDismiss={chain.dismissChainNotice} />
        {dropdownPosition && suggestions.length > 0 && (
          <div
            className="suggestion-dropdown"
            style={{
              // Viewport coordinates, so a scrolled report cannot move the menu away
              // from the word that opened it.
              position: 'fixed',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              backgroundColor: '#fff',
              border: '1px solid #ccc',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
              borderRadius: '4px',
              color: 'black',
              zIndex: 1000,
              padding: '3px',
              minWidth: '50px',
              maxWidth: '350px', // Max width
              maxHeight: '200px', // Max height before scrolling
              overflowY: 'auto', // Enables scrolling if content overflows
            }}
          >
            {suggestions.map((suggestion, index) => (
              <div
                key={index}
                onClick={() => handleReplaceWord(suggestion)}
                style={{
                  padding: '4px',
                  cursor: 'pointer',
                  borderBottom: index !== suggestions.length - 1 ? '1px solid #ddd' : 'none',
                  fontSize: '12px',
                }}
              >
                {suggestion}
              </div>
            ))}
          </div>
        )}
        {dynamicFieldPosition &&
          dynamicFieldSlot?.fieldType === 'dropdown' &&
          dynamicFieldSlot.options.length > 0 && (
            <div
              className="suggestion-dropdown dynamic-field-popup"
              style={{
                position: 'fixed',
                top: `${dynamicFieldPosition.top}px`,
                left: `${dynamicFieldPosition.left}px`,
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                borderRadius: '4px',
                color: 'black',
                zIndex: 1000,
                padding: '3px',
                width: `${SUGGESTION_POPUP_WIDTH}px`,
                maxWidth: 'calc(100% - 24px)',
                boxSizing: 'border-box',
                maxHeight: '200px',
                overflowY: 'auto',
              }}
            >
              {dynamicFieldSlot.options.map((opt, index) => (
                <div
                  key={opt}
                  onClick={() => handleCommitDynamicField(opt)}
                  style={{
                    padding: '4px',
                    cursor: 'pointer',
                    borderBottom:
                      index !== dynamicFieldSlot.options.length - 1 ? '1px solid #ddd' : 'none',
                    fontSize: '12px',
                  }}
                >
                  {opt}
                </div>
              ))}
            </div>
          )}
        {dynamicFieldPosition &&
          dynamicFieldSlot &&
          !(dynamicFieldSlot.fieldType === 'dropdown' && dynamicFieldSlot.options.length > 0) && (
            <div
              className="dynamic-field-popup"
              style={{
                position: 'fixed',
                top: `${dynamicFieldPosition.top}px`,
                left: `${dynamicFieldPosition.left}px`,
                width: `${CHAIN_POPUP_WIDTH}px`,
                maxWidth: 'calc(100% - 24px)',
                boxSizing: 'border-box',
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                borderRadius: '4px',
                color: 'black',
                zIndex: 1000,
                padding: '8px',
              }}
            >
              <div
                style={{ fontSize: '11px', color: '#5b6b80', marginBottom: '4px', fontWeight: 600 }}
              >
                {dynamicFieldSlot.label}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input
                  autoFocus
                  inputMode={dynamicFieldSlot.fieldType === 'number' ? 'decimal' : undefined}
                  value={dynamicFieldDraft}
                  onChange={e => setDynamicFieldDraft(e.target.value)}
                  onBlur={() => {
                    if (dynamicFieldCancelledRef.current) {
                      return;
                    }
                    handleCommitDynamicField();
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCommitDynamicField();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      dynamicFieldCancelledRef.current = true;
                      closeDynamicField();
                    }
                  }}
                  style={{
                    width: '100%',
                    fontSize: '13px',
                    padding: '4px 6px',
                    border: '1px solid #ccc',
                    borderRadius: '3px',
                    outline: 'none',
                  }}
                />
                {dynamicFieldSlot.fieldType === 'number' && dynamicFieldSlot.unit && (
                  <span style={{ fontSize: '12px', color: '#5b6b80', whiteSpace: 'nowrap' }}>
                    {dynamicFieldSlot.unit}
                  </span>
                )}
              </div>
              <div
                style={{ marginTop: '5px', fontSize: '10px', color: '#8494a8', minHeight: '13px' }}
              >
                Applied when you click away.
              </div>
            </div>
          )}
        {numericPosition && numericSlot && (
          <div
            className="numeric-field-popup"
            style={{
              position: 'fixed',
              top: `${numericPosition.top}px`,
              left: `${numericPosition.left}px`,
              backgroundColor: '#fff',
              border: '1px solid #ccc',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
              borderRadius: '4px',
              color: 'black',
              zIndex: 1000,
              padding: '8px',
              width: `${NUMERIC_POPUP_WIDTH}px`,
              maxWidth: 'calc(100% - 24px)',
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{ fontSize: '11px', color: '#5b6b80', marginBottom: '4px', fontWeight: 600 }}
            >
              {NUMERIC_REPORT_FIELDS[numericSlot.key]?.label || numericSlot.key}
            </div>
            {stressSource && numericDraft.trim() !== '' && (
              <div
                style={{ fontSize: '10px', color: '#8494a8', marginBottom: '4px' }}
                title={stressSource.file}
              >
                from attached {stressSource.format}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input
                autoFocus
                value={numericDraft}
                placeholder={NUMERIC_REPORT_FIELDS[numericSlot.key]?.placeholder || ''}
                onChange={e => setNumericDraft(e.target.value)}
                onBlur={() => {
                  if (numericCancelledRef.current) return;
                  // Only a finished entry is applied; a half-typed one is discarded rather
                  // than written into the report. Enter also commits an EMPTY value, which
                  // restores the "--" marker — deliberate intent, unlike a stray click.
                  if (isCompleteNumericEntry(numericDraft)) {
                    handleCommitNumeric();
                  } else {
                    setNumericPosition(null);
                    setNumericSlot(null);
                  }
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (isCompleteNumericEntry(numericDraft) || numericDraft.trim() === '') {
                      handleCommitNumeric();
                    }
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    numericCancelledRef.current = true;
                    setNumericPosition(null);
                    setNumericSlot(null);
                  }
                }}
                style={{
                  width: '100%',
                  fontSize: '13px',
                  padding: '4px 6px',
                  border: `1px solid ${
                    numericDraft.trim() !== '' && !isCompleteNumericEntry(numericDraft)
                      ? '#d19a2a'
                      : '#ccc'
                  }`,
                  borderRadius: '3px',
                  outline: 'none',
                }}
              />
              <span style={{ fontSize: '12px', color: '#5b6b80', whiteSpace: 'nowrap' }}>
                {NUMERIC_REPORT_FIELDS[numericSlot.key]?.unit}
              </span>
            </div>
            <div
              style={{
                marginTop: '5px',
                fontSize: '10px',
                color:
                  numericDraft.trim() !== '' && !isCompleteNumericEntry(numericDraft)
                    ? '#b8801a'
                    : '#8494a8',
                minHeight: '13px',
              }}
            >
              {numericDraft.trim() !== '' && !isCompleteNumericEntry(numericDraft)
                ? 'Finish the value to apply it.'
                : 'Applied when you click away.'}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export const PatientReportIndex = React.forwardRef((props, ref) => {
  // const [imageSrc, setImageSrc] = useState<string>();
  const { reportData, setHitGetReportApi, setUpdatedTemplateContent } = props;
  // TipTap accepts an empty HTML string as blank content. Passing `{}` while
  // the report request is hydrating produces "Unknown node type: undefined".
  const [editorContent, setEditorContent] = useState<any>('');
  const [ReportData, setReportData] = useState({});
  const [initialLoading, setInitialLoading] = useState(true);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [lastSavedContent, setLastSavedContent] = useState<any>(null);
  const [lastSavedColorCodes, setLastSavedColorCodes] = useState<any>(null);
  const [isAutoSaveReady, setIsAutoSaveReady] = useState(false);
  const [isUpdateable, setIsUpdateable] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [reportAssignedTo, setReportAssignedTo] = useState('');
  const [isSonographerModalOpen, setIsSonographerModalOpen] = useState(false);
  /** Sonographer of record for this report, as shown in the body's "Sonographer:" row. */
  const [sonographer, setSonographer] = useState('');

  const [isAssigning, setIsAssigning] = useState(false);
  const [isAttachClipsOpen, setIsAttachClipsOpen] = useState(false);
  const [attachedClips, setAttachedClips] = useState<any[]>([]);
  const [sharedClipsUrl, setSharedClipsUrl] = useState<string | null>(null);
  const [sharedClipsName, setSharedClipsName] = useState('');
  const role = localStorage.getItem('role') || sessionStorage.getItem('role');
  const dispatch = useDispatch<AppDispatch>();

  const isUpdateableCondition = () => {
    const userId = sessionStorage.getItem('userId') || localStorage.getItem('userId');
    const userName = sessionStorage.getItem('name') || localStorage.getItem('name');
    if (!isAssignedDoctor(role, reportData?.data?.reportAssignedToId, userId, reportData?.data?.reportAssignedTo, userName)) return false;
    return canEditMedicalData(role, reportData?.data?.status);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 600);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (Object.keys(reportData).length) {
      setReportData(reportData);
      setIsUpdateable(isUpdateableCondition());
      setReportAssignedTo(reportData?.data?.reportAssignedTo || '');
      setSonographer(reportData?.data?.createdByName || '');
      setAttachedClips(reportData?.data?.attachedClips || []);
      setSharedClipsUrl(reportData?.data?.sharedClipsUrl || null);
      setSharedClipsName(reportData?.data?.sharedClipsName || '');
    }
  }, [reportData, role]);

  /**
   * Attach the study's clips automatically here too (Nikhil, 1 Sep). `isUpdateable`
   * already carries the assigned-doctor and status checks for this page, so it is the
   * whole gate. Runs once per report and never on one that has had clips saved before.
   */
  useAutoAttachClips({
    reportId: reportData?.data?._id,
    studyInstanceUID: reportData?.data?.studyInstanceUID,
    attachedClips,
    sharedClipsUrl,
    enabled: isUpdateable,
    onAttached: (clips, shareUrl, name) => {
      setAttachedClips(clips);
      if (shareUrl) setSharedClipsUrl(shareUrl);
      if (name) setSharedClipsName(name);
    },
  });

  /**
   * Atrial-fibrillation mark for this study.
   *
   * Passed to the wording engine because the diastolic-function rule assumes sinus
   * rhythm — E/A means nothing without an A wave — so that one sentence stays silent in
   * AF rather than grading on a ratio that does not apply. Held per clinic in the same
   * document as the critical and research marks, so one fetch covers all of them.
   */
  const [isAF, setIsAF] = useState(false);
  useEffect(() => {
    const uid = reportData?.data?.studyInstanceUID;
    if (!uid) {
      setIsAF(false);
      return;
    }
    dispatch(getStudyMarkingsApi())
      .unwrap()
      .then((res: any) => setIsAF((res?.data?.afList || []).includes(uid)))
      .catch(() => setIsAF(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, reportData?.data?.studyInstanceUID]);

  // Color codes restored only when a DIFFERENT report loads — not on every reportData
  // refresh (e.g. after finalize/unfinalize). Using [reportData, role] caused Redux
  // dispatches post-save to re-trigger this and overwrite locally-edited diagram values.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (Object.keys(reportData).length) {
      setRestColorCodes(reportData?.data?.colorCode?.restColorCodes);
      setStressColorCodes(reportData?.data?.colorCode?.stressColorCodes);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportData?.data?._id, role]);

  // useEffect(() => {
  //   if (ReportData?.hospitalLogo) {
  //     convertBlobToBase64(ReportData?.hospitalLogo)
  //       .then(base64Image => setImageSrc(base64Image))
  //       .catch(error => console.error('Error converting blob to base64:', error));
  //   }
  // }, [ReportData?.hospitalLogo]);

  // Saves an edit still inside the 2s autosave debounce, so a PDF rendered by the
  // server straight after typing includes it. Resolves false if the save failed.
  React.useImperativeHandle(ref, () => ({
    flushPendingChanges: async () => {
      const f = autoSaveFlushRef.current;
      if (!f.id || !f.updateable || !f.ready || !f.content || Object.keys(f.content).length === 0) {
        return true;
      }
      if (
        JSON.stringify(f.content) === JSON.stringify(f.lastSaved) &&
        JSON.stringify(f.colors) === JSON.stringify(f.lastSavedColors)
      ) {
        return true;
      }
      try {
        await dispatch(
          updateReport({ reportId: f.id, templateHTML: f.content, colorCode: f.colors })
        ).unwrap();
        setLastSavedContent(f.content);
        setLastSavedColorCodes(f.colors);
        dispatch(clearState());
        return true;
      } catch {
        return false;
      }
    },
  }));

  const [restColorCodes, setRestColorCodes] = useState({});
  const [stressColorCodes, setStressColorCodes] = useState({});

  const debouncedContent = useDebounce(editorContent, 2000);
  const debouncedRestColorCodes = useDebounce(restColorCodes, 2000);
  const debouncedStressColorCodes = useDebounce(stressColorCodes, 2000);

  useEffect(() => {
    setLastSavedContent(null);
    setIsAutoSaveReady(false);
    setAutoSaveStatus('idle');
  }, [ReportData?.data?._id]);

  // Baseline = the hydrated (loaded) content, captured IMMEDIATELY. The old
  // debounced priming meant an edit made within the first 2s window became the
  // baseline itself — marked "already saved" and silently never persisted
  // (report changes lost unless a finalize happened to carry them).
  useEffect(() => {
    if (isAutoSaveReady) return;
    if (!editorContent || Object.keys(editorContent).length === 0) return;
    setLastSavedContent(editorContent);
    setLastSavedColorCodes({ restColorCodes: { ...restColorCodes }, stressColorCodes: { ...stressColorCodes } });
    setIsAutoSaveReady(true);
  }, [editorContent, isAutoSaveReady]);

  // Flush any pending (sub-debounce) edit on unmount — switching tabs unmounts
  // this editor and used to drop up to 2s of typing.
  const autoSaveFlushRef = useRef<any>({});
  useEffect(() => {
    autoSaveFlushRef.current = {
      id: ReportData?.data?._id,
      updateable: isUpdateable,
      ready: isAutoSaveReady,
      content: editorContent,
      lastSaved: lastSavedContent,
      lastSavedColors: lastSavedColorCodes,
      colors: { restColorCodes: { ...restColorCodes }, stressColorCodes: { ...stressColorCodes } },
    };
  });
  useEffect(
    () => () => {
      const f = autoSaveFlushRef.current;
      if (!f.id || !f.updateable || !f.ready || !f.content || Object.keys(f.content).length === 0) {
        return;
      }
      if (JSON.stringify(f.content) === JSON.stringify(f.lastSaved)) return;
      dispatch(updateReport({ reportId: f.id, templateHTML: f.content, colorCode: f.colors }));
    },
    []
  );

  useEffect(() => {
    if (
      !ReportData?.data?._id ||
      !debouncedContent ||
      !isUpdateable ||
      Object.keys(debouncedContent).length === 0
    ) {
      return;
    }
    if (!isAutoSaveReady) return;
    if (autoSaveStatus === 'saving') return;

    const contentUnchanged = JSON.stringify(debouncedContent) === JSON.stringify(lastSavedContent);
    const colorCodesUnchanged =
      JSON.stringify({ restColorCodes: debouncedRestColorCodes, stressColorCodes: debouncedStressColorCodes }) ===
      JSON.stringify(lastSavedColorCodes);

    if (contentUnchanged && colorCodesUnchanged) return;

    const performAutoSave = async () => {
      setAutoSaveStatus('saving');
      try {
        await dispatch(
          updateReport({
            reportId: ReportData?.data?._id,
            templateHTML: debouncedContent,
            colorCode: {
              restColorCodes: debouncedRestColorCodes,
              stressColorCodes: debouncedStressColorCodes,
            },
          })
        ).unwrap();
        setLastSavedContent(debouncedContent);
        setLastSavedColorCodes({ restColorCodes: { ...debouncedRestColorCodes }, stressColorCodes: { ...debouncedStressColorCodes } });
        dispatch(clearState());
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus('idle'), 2000);
      } catch {
        setAutoSaveStatus('error');
      }
    };
    void performAutoSave();
  }, [
    ReportData?.data?._id,
    debouncedContent,
    debouncedRestColorCodes,
    debouncedStressColorCodes,
    isUpdateable,
    isAutoSaveReady,
    autoSaveStatus,
    lastSavedContent,
    lastSavedColorCodes,
    dispatch,
    setHitGetReportApi,
  ]);

  const AutoSaveStatusIndicator = () => {
    if (autoSaveStatus === 'idle') return null;
    return (
      <div className="auto-save-status text-xs text-gray-600 mt-2">
        {autoSaveStatus === 'saving' && 'Saving...'}
        {autoSaveStatus === 'saved' && 'All changes saved'}
        {autoSaveStatus === 'error' && 'Auto-save failed'}
      </div>
    );
  };

  return (
    <>
      <div
        className="whiteTemplateBox"
        id="whiteTemplateBox"
      >
        <div className="mb-4 flex items-center justify-between">
          <div
            className=""
            style={{ height: '150px', width: '200px', overflow: 'content' }}
          >
            {/* <img
              src={imageSrc}
              alt="Hospital Logo"
              className="h-full w-full object-contain"
            /> */}
            <img
              src={reportData?.hospitalLogo}
              alt="Hospital Logo"
              className="h-full w-full object-contain"
            />
          </div>
          <div className="text-normal text-right text-black">
            <p style={{ fontSize: '16px', fontWeight: '600' }}>
              {capitalizeFirstLetterOfEachWord(ReportData?.hospitalHeader?.hospitalName)}
            </p>
            <p>
              {ReportData?.hospitalHeader?.address}
              {ReportData?.hospitalHeader?.city || ReportData?.hospitalHeader?.zipCode
                ? ` - ${[ReportData?.hospitalHeader?.city, ReportData?.hospitalHeader?.zipCode]
                    .filter(Boolean)
                    .join(', ')}`
              : ''}
            </p>
            <p>
              <span>
                <strong>Phone No : </strong>({ReportData?.hospitalHeader?.contactNumberCountryCode}){' '}
                {ReportData?.hospitalHeader?.contactNumber}{', '}
              </span>
              {ReportData?.hospitalHeader?.fax && (
                <span>
                  <strong>Fax : </strong>
                  {ReportData?.hospitalHeader?.fax}
                </span>
              )}
            </p>
            {ReportData?.hospitalHeader?.email && (
              <p>
                <strong>Email : </strong> {ReportData?.hospitalHeader?.email}
              </p>
            )}
            {ReportData?.hospitalHeader?.website && (
              <p>
                <strong>Website : </strong>{' '}
                <a
                  href={ReportData?.hospitalHeader?.website}
                  style={{ color: 'black' }}
                >
                  {ReportData?.hospitalHeader?.website}
                </a>
              </p>
            )}
          </div>
        </div>
        <ReportLayout
          reportTemplate={ReportData}
          setEditorContent={setEditorContent}
          editorContent={editorContent}
          setUpdatedTemplateContent={setUpdatedTemplateContent}
          isUpdateable={isUpdateable}
          atrialFibrillation={isAF}
          reportAssignedTo={reportAssignedTo}
          onAssignCardiologist={ReportData?.data?._id && role === 'Sonographer' ? () => setIsAssignModalOpen(true) : undefined}
          sonographer={sonographer}
          onSelectSonographer={
            ReportData?.data?._id && role === 'Sonographer'
              ? () => setIsSonographerModalOpen(true)
              : undefined
          }
        />
        {sharedClipsUrl && attachedClips.length > 0 ? (
          <div className="attached-clips-section">
            <div className="attached-clips-section__header">
              <h4>Clips Attached - {attachedClips.length}</h4>
            </div>
            <ul className="attached-clips-section__list">
              <li>
                <a
                  href={sharedClipsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  See Clips
                </a>
              </li>
            </ul>
          </div>
        ) : null}
        {isUpdateable && ReportData?.data?._id && (
          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              className="btn btn__primary"
              onClick={() => setIsAttachClipsOpen(true)}
            >
              Change Clips
            </button>
          </div>
        )}
        {(reportData?.data?.reportType === 'Stress Echo' ||
          reportData?.data?.reportType === 'Dobutamine Stress Echo') && (
            <div className="report-diagrams">
              <div className="target">
                <RestBullsEyePlot restColorCodes={restColorCodes} />
                <StressBullsEyePlot stressColorCodes={stressColorCodes} />
                <TargetCodes />
              </div>
              <div className="rest-heart-chambers">
                <p className="title title--h5 title--semibold">Rest</p>
                <div className="rest-heart-chambers__grid">
                  <div className="item">
                    <Apical2Ch
                      setRestColorCodes={setRestColorCodes}
                      restColorCodes={restColorCodes}
                    />
                  </div>
                  <div className="item">
                    <Apical3Ch
                      setRestColorCodes={setRestColorCodes}
                      restColorCodes={restColorCodes}
                    />
                  </div>
                  <div className="item">
                    <Apical4Ch
                      setRestColorCodes={setRestColorCodes}
                      restColorCodes={restColorCodes}
                    />
                  </div>
                </div>
              </div>
              <div className="rest-heart-chambers">
                <p className="title title--h5 title--semibold">Stress</p>
                <div className="rest-heart-chambers__grid">
                  <div className="item">
                    <StressApical2Ch
                      stressColorCodes={stressColorCodes}
                      setStressColorCodes={setStressColorCodes}
                    />
                  </div>
                  <div className="item">
                    <StressApical3Ch
                      stressColorCodes={stressColorCodes}
                      setStressColorCodes={setStressColorCodes}
                    />
                  </div>
                  <div className="item">
                    <StressApical4Ch
                      stressColorCodes={stressColorCodes}
                      setStressColorCodes={setStressColorCodes}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
      </div>
      {isUpdateable && (
        <div className="mt-4">
          <AutoSaveStatusIndicator />
        </div>
      )}
      <Loader isLoading={initialLoading} />
      {isAttachClipsOpen && ReportData?.data?._id && (
        <AttachClipsModal
          open={isAttachClipsOpen}
          reportId={ReportData.data._id}
          studyInstanceUID={ReportData?.data?.studyInstanceUID}
          initialAttachedClips={attachedClips}
          initialSharedClipsName={sharedClipsName}
          onClose={() => setIsAttachClipsOpen(false)}
          onSaved={(clips, shareUrl, name) => {
            setAttachedClips(clips);
            if (shareUrl) setSharedClipsUrl(shareUrl);
            setSharedClipsName(name || '');
          }}
        />
      )}
      {isSonographerModalOpen && ReportData?.data?._id && (
        <AssignSonographerModal
          isOpen={isSonographerModalOpen}
          reportId={ReportData.data._id}
          onClose={() => setIsSonographerModalOpen(false)}
          onSelected={name => {
            setSonographer(name);
            setIsSonographerModalOpen(false);
          }}
        />
      )}
      {isAssignModalOpen && ReportData?.data?._id && (
        <AssignDoctorModal
          reportId={ReportData.data._id}
          AssignReportStatus={reportAssignedTo ? 'Reassign' : 'Assign'}
          isAssignDoctorModalOpen={isAssignModalOpen}
          setOnAssigningReport={setIsAssigning}
          onClose={() => setIsAssignModalOpen(false)}
          onAssignSuccess={doctorName => {
            setReportAssignedTo(doctorName);
            setIsAssignModalOpen(false);
          }}
        />
      )}

    </>
  );
});
